/**
 * Sign up page component.
 *
 * User registration page with comprehensive form validation including email,
 * username, password strength requirements, and activation code. Provides
 * real-time password strength feedback and validation error messages.
 *
 * Route: `/signup`
 * Requires authentication: No
 *
 * @module SignUp
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen, Eye, EyeOff, Check, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { isValidEmail, isValidUsername } from "@/lib/validation";
import { logger } from "@/lib/logger";
import { ROUTES } from "@/lib/routeConstants";
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BackToLandingLink } from "@/components/layout/BackToLandingLink";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

// "Sign up with Google" is only shown when a Client ID is configured, so the
// page is unchanged when the feature is off.
const GOOGLE_ENABLED = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;

// Password strength indicator component
interface PasswordStrengthIndicatorProps {
  checks: {
    length: boolean;
    lowercase: boolean;
    uppercase: boolean;
    number: boolean;
    special: boolean;
  };
}

interface RequirementItemProps {
  met: boolean;
  label: string;
}

function RequirementItem({ met, label }: Readonly<RequirementItemProps>) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <Check className="h-3 w-3 text-green-500" />
      ) : (
        <X className="h-3 w-3 text-red-500" />
      )}
      <span className={met ? "text-green-600" : "text-red-600"}>
        {label}
      </span>
    </div>
  );
}

function PasswordStrengthIndicator({ checks }: Readonly<PasswordStrengthIndicatorProps>) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <div className="text-sm text-muted-foreground">
        {t('auth.passwordStrength.title', { defaultValue: 'Password Strength Requirements:' })}
      </div>
      <div className="space-y-1">
        <RequirementItem met={checks.length} label={t('auth.passwordStrength.length', { defaultValue: 'At least 8 characters' })} />
        <RequirementItem met={checks.lowercase} label={t('auth.passwordStrength.lowercase', { defaultValue: 'One lowercase letter' })} />
        <RequirementItem met={checks.uppercase} label={t('auth.passwordStrength.uppercase', { defaultValue: 'One uppercase letter' })} />
        <RequirementItem met={checks.number} label={t('auth.passwordStrength.number', { defaultValue: 'One number' })} />
        <RequirementItem met={checks.special} label={t('auth.passwordStrength.special', { defaultValue: 'One special character' })} />
      </div>
    </div>
  );
}

export default function SignUp() {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [dataProcessingConsent, setDataProcessingConsent] = useState(false);
  const [voiceRecordingConsent, setVoiceRecordingConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleGoogleCredential = async (idToken: string) => {
    setIsLoading(true);
    try {
      const result = await googleLogin(idToken);
      if (result.success) {
        toast.success(t('auth.success.accountCreated'), {
          description: t('auth.success.accountCreatedDesc'),
        });
        navigate(ROUTES.DASHBOARD);
      } else {
        const isRateLimited =
          result.code === "RATE_LIMIT_EXCEEDED" || result.status === 429;
        toast.error(
          isRateLimited ? t('auth.errors.tooManyAttempts') : t('auth.errors.signInFailed'),
          { description: result.message },
        );
      }
    } catch (error) {
      logger.error("Google sign-up error", error);
      toast.error(t('auth.errors.connectionError'), {
        description: t('auth.errors.connectionErrorDesc'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Password strength validation
  const getPasswordStrength = (password: string) => {
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const score = Object.values(checks).filter(Boolean).length;
    return { checks, score };
  };

  const passwordStrength = getPasswordStrength(password);
  const isPasswordStrong = passwordStrength.score === 5;

  // Validation helper
  const validateForm = (): boolean => {
    if (!isValidUsername(username)) {
      toast.error(t('auth.errors.invalidUsername'), {
        description: t('auth.errors.usernameRequirements'),
      });
      return false;
    }

    if (!isValidEmail(email)) {
      toast.error(t('auth.errors.invalidEmail'), {
        description: t('auth.errors.invalidEmailDesc'),
      });
      return false;
    }

    if (!isPasswordStrong) {
      toast.error(t('auth.errors.passwordTooWeak'), {
        description: t('auth.errors.checkRequirements'),
      });
      return false;
    }

    if (password !== confirmPassword) {
      toast.error(t('auth.errors.passwordsMismatch'), {
        description: t('auth.errors.passwordsMismatchDesc'),
      });
      return false;
    }

    if (!dataProcessingConsent) {
      toast.error(t('auth.errors.consentRequired', { defaultValue: 'Data processing consent is required' }), {
        description: t('auth.errors.consentRequiredDesc', { defaultValue: 'You must agree to data processing to create an account.' }),
      });
      return false;
    }

    return true;
  };

  // Error message mapping helper
  const mapErrorMessage = (errorMessage: string): string => {
    if (errorMessage.includes("Both email address and username are already taken")) {
      return t('auth.errors.duplicateEmailAndUsername', { defaultValue: "Both email address and username are already taken. Please use different credentials." });
    }
    if (errorMessage.includes("Email address is already registered")) {
      return t('auth.errors.duplicateEmail', { defaultValue: "This email address is already registered. Please sign in or use a different email." });
    }
    if (errorMessage.includes("Username is already taken")) {
      return t('auth.errors.duplicateUsername', { defaultValue: "This username is already taken. Please choose a different username." });
    }
    if (errorMessage.includes("already exists")) {
      return t('auth.errors.duplicateUser', { defaultValue: "An account with this email or username already exists. Please sign in instead or use different credentials." });
    }
    return errorMessage;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await register(
        email,
        password,
        username,
        dataProcessingConsent,
        voiceRecordingConsent,
      );

      if (result.success) {
        toast.success(t('auth.success.accountCreated'), {
          description: t('auth.success.accountCreatedDesc'),
        });
        navigate(ROUTES.DASHBOARD);
      } else {
        const errorMessage =
          result.error ||
          "Unable to create account. Please check your information and try again.";
        const userMessage = mapErrorMessage(errorMessage);

        toast.error(userMessage);
      }
    } catch (error) {
      logger.error("Registration error", error);
      toast.error(t('auth.errors.connectionError'), {
        description: t('auth.errors.connectionErrorDesc'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-start bg-background px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-16 sm:py-8">
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-4">
          <BackToLandingLink useHistoryBack />
        </div>
        {/* Logo */}
        <div className="text-center mb-6 sm:mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="bg-primary p-2 sm:p-3 rounded-lg shadow-sm">
              <BookOpen className="h-6 w-6 sm:h-8 sm:w-8 text-primary-foreground" />
            </div>
            <div className="text-left leading-tight">
              <h1 className="font-bold text-xl sm:text-2xl text-foreground">
                DeutschGenie
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {t('common.slogan')}
              </p>
            </div>
          </Link>
        </div>

        <Card className="shadow-sm border-0 sm:border">
          <CardHeader className="text-center pb-4 py-4 sm:py-6">
            <CardTitle className="text-xl sm:text-2xl">{t('auth.signUp.title', { defaultValue: 'Sign Up' })}</CardTitle>
            <CardDescription className="text-sm">
              {t('auth.signUp.description', { defaultValue: 'Create a new account to get started' })}
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-3 sm:space-y-4 px-4 sm:px-6">
              <div className="space-y-2">
                <Label htmlFor="username">{t('auth.signUp.username')}</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder={t('auth.signUp.usernamePlaceholder')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.signUp.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.signUp.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.signUp.password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t('auth.signUp.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={
                      password && !isPasswordStrong
                        ? "border-destructive"
                        : ""
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {/* Password Strength Indicator */}
                {password && (
                  <PasswordStrengthIndicator checks={passwordStrength.checks} />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('auth.signUp.confirmPassword')}</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t('auth.signUp.confirmPasswordPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              {/* Mandatory: data processing consent */}
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="data-processing"
                    checked={dataProcessingConsent}
                    onCheckedChange={(checked) => setDataProcessingConsent(checked === true)}
                    aria-required="true"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="data-processing" className="font-medium cursor-pointer text-sm">
                      {t('consent.page.dataProcessingLabel', { defaultValue: 'I agree to data processing' })}
                      <span className="ml-2 text-xs text-destructive font-normal">
                        {t('consent.page.dataProcessingRequired', { defaultValue: 'Required' })}
                      </span>
                    </Label>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t('consent.page.dataProcessingDisclosure', {
                        defaultValue:
                          'By using DeutschGenie, you agree that simulation results, practice results, and session data may be processed by external AI APIs (such as OpenAI) for analysis and personalised feedback.',
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Optional: voice recording consent */}
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="voice-recording"
                    checked={voiceRecordingConsent}
                    onCheckedChange={(checked) => setVoiceRecordingConsent(checked === true)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="voice-recording" className="font-medium cursor-pointer text-sm">
                      {t('consent.page.voiceRecordingLabel', { defaultValue: 'I agree to voice recording' })}
                    </Label>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t('consent.page.voiceRecordingDisclosure', {
                        defaultValue:
                          'Your voice may be recorded during simulation sessions. Recordings are used for simulation and practice-result analysis. Recordings are retained for 7 days and are automatically deleted after 7 days.',
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4 px-4 sm:px-6 pb-6 sm:pb-8">
              <Button
                type="submit"
                className="w-full"
                variant="default"
                disabled={isLoading || !dataProcessingConsent}
              >
                {isLoading ? t('auth.signUp.submitting', { defaultValue: 'Creating account...' }) : t('auth.signUp.submit', { defaultValue: 'Sign Up' })}
              </Button>
              {GOOGLE_ENABLED && (
                <>
                  <div className="relative w-full">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">
                        {t('auth.or', { defaultValue: 'or' })}
                      </span>
                    </div>
                  </div>
                  <GoogleSignInButton onCredential={handleGoogleCredential} />
                  <p className="text-xs text-muted-foreground text-center">
                    {t('auth.googleConsentNotice', { defaultValue: 'By continuing with Google you agree to our data processing terms.' })}
                  </p>
                </>
              )}
              <div className="text-center text-sm">
                <span className="text-muted-foreground">
                  {t('auth.signUp.hasAccount', { defaultValue: 'Already have an account?' })}{" "}
                </span>
                <Link
                  to={ROUTES.SIGN_IN}
                  className="text-primary hover:underline font-medium"
                >
                  {t('auth.signUp.signInLink', { defaultValue: 'Sign in' })}
                </Link>
              </div>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
